import { createAsyncSelectors } from '@lib/selectors';
import { useAction } from '../useAction';
import { useImportStatus } from './useImportStatus';
import { useInterval } from '../useInterval';
import { useLatestSalesforceSync } from './useLatestSalesforceSync';
import { useOnboardingProgress } from '../useOnboardingProgress';
import { useSonarSelector } from '../useSonarSelector';
import { Import, importsActions } from '@sonar-software/sonar-imports';
import { isSyncCompleted, isSyncFailed, isSyncInProgress, isSyncWaiting } from '@sonar-software/sonar-ts-lib';
import { useCallback, useEffect, useMemo } from 'react';

export interface UseSalesforceSyncResult {
	importId?: number | null;
	isRequested: boolean;
	isSyncing: boolean;
	resetSync: () => void;
	startSync: () => void;
	status?: Import | null;
}

function getCurrentStatus(current?: Import | null, latest?: Import | null): string | null | undefined {
	if (!current) {
		return latest?.status;
	}

	if (!latest) {
		return current?.status;
	}

	if (latest.id >= current.id) {
		return latest.status;
	}

	return current.status;
}

const salesforceImportSelectors = createAsyncSelectors((state) => state.imports.salesforce.start.post);

export function useSalesforceSync(enableRefresh?: boolean): UseSalesforceSyncResult {
	const startResult = useSonarSelector(salesforceImportSelectors.data);
	const isRequested = useSonarSelector(salesforceImportSelectors.isRequested);

	const latestSync = useLatestSalesforceSync();
	const { updateProgress, progress } = useOnboardingProgress();
	const { status, refresh } = useImportStatus(startResult?.id ?? progress?.currentlyRunningSfdcImportId, 1);

	const startSync = useAction(importsActions.requestPostImportsSalesforceStart);
	const resetSync = useAction(importsActions.resetPostImportsSalesforceStart);

	useInterval(() => {
		if (!enableRefresh || !status || isSyncFailed(status?.status) || isSyncCompleted(status?.status)) {
			return;
		}

		refresh();
	}, 5000);

	useEffect(() => {
		if (!progress || !isSyncWaiting(status?.status) || !isSyncInProgress(status?.status)) {
			return;
		}

		const runningImportId = status?.id || (isSyncInProgress(latestSync?.status) ? latestSync?.id : null);

		if (
			!progress.currentlyRunningSfdcImportId &&
			runningImportId &&
			runningImportId !== progress.latestSuccessfulSfdcImportId
		) {
			updateProgress({ currentlyRunningSfdcImportId: runningImportId });
		}
	}, [ latestSync?.id, latestSync?.status, progress, status?.id, status?.status, updateProgress ]);

	const isSyncing = useMemo(() => {
		const currentStatus = getCurrentStatus(status, latestSync);

		return isSyncWaiting(currentStatus) || isSyncInProgress(currentStatus);
	}, [ latestSync, status ]);

	const startSyncEmptyRequest = useCallback(() => {
		startSync({});
	}, [ startSync ]);

	return {
		importId: startResult?.id,
		isRequested,
		isSyncing,
		resetSync,
		startSync: startSyncEmptyRequest,
		status
	};
}
